import styled from 'styled-components';
import Typography from '../../../kingpin/atoms/Typography';
import Row from '../../Common/Row';
import Icon from '../../../kingpin/atoms/Icon';
import Tooltip from '../../Tooltip';
import RewardBandsTooltip from './RewardBandsTooltip';
import Loading from '../../Loading';
import { ReactNode, useMemo } from 'react';
import { isArnoldBrosRewardBands } from '../utils/rewardBandsUtils';

const Subtitle = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-style: Medium;
  font-size: 12px;
  leading-trim: NONE;
  line-height: 100%;
  letter-spacing: 0%;
  vertical-align: middle;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-decoration-offset: 0%;
  text-decoration-thickness: 0%;

  color: var(--color-text-secondary);
`;

const RewardPay = ({
  isEligible,
  currentPay,
  paySubTitle,
  subtitleNode,
  isPassed,
  rewardBands,
  rewardType,
  isOverridden,
  payInfoTooltip,
  payInfoTooltipTestId,
  rewardBandsTooltipTestId,
}: {
  isEligible: boolean;
  currentPay: string;
  paySubTitle?: string;
  // When provided, replaces the static rate subtitle line (e.g. the override-mode dropdown).
  subtitleNode?: ReactNode;
  isPassed: boolean;
  rewardBands?:
    | BonusTypes.Rewards.RewardBands
    | BonusTypes.Rewards.ArnoldBrosRewardBands;
  rewardType: 'none' | 'points' | 'monetary';
  // A reward-level payout override is active on this line (staged or persisted).
  isOverridden?: boolean;
  // Info affordance next to the pay figure (e.g. an aggregated reward's component breakdown).
  payInfoTooltip?: ReactNode;
  payInfoTooltipTestId?: string;
  rewardBandsTooltipTestId?: string;
}) => {
  const PayTooltip = useMemo(() => {
    if (!isEligible) {
      return 'This driver is not eligible for the bonus';
    }

    return undefined;
  }, [isEligible]);

  return (
    <>
      <Row centerAlign style={{ gap: 4 }}>
        <Tooltip content={PayTooltip}>
          <Typography.Header
            type={'H3'}
            color={
              isPassed && isEligible ? undefined : 'var(--color-text-muted)'
            }
          >
            {currentPay}
          </Typography.Header>
        </Tooltip>
        {!!rewardBands &&
          (rewardType === 'points' || isArnoldBrosRewardBands(rewardBands)) && (
            <Tooltip
              content={<RewardBandsTooltip rewardBands={rewardBands} />}
              position={
                isArnoldBrosRewardBands(rewardBands) ? 'left' : undefined
              }
              testId={rewardBandsTooltipTestId}
            >
              <Icon icon={'info'} color={'var(--color-icon-secondary)'} />
            </Tooltip>
          )}
        {!!payInfoTooltip && (
          <Tooltip
            content={payInfoTooltip}
            testId={payInfoTooltipTestId}
            isUnstyledTooltip
          >
            <Icon icon={'info'} color={'var(--color-icon-secondary)'} />
          </Tooltip>
        )}
      </Row>
      {(subtitleNode || !!paySubTitle || isOverridden) && (
        <Row centerAlign style={{ gap: 4 }}>
          {isOverridden && (
            <Tooltip content={'Payout overridden'}>
              <Icon icon={'info'} color={'var(--color-status-warning)'} />
            </Tooltip>
          )}
          {subtitleNode
            ? subtitleNode
            : !!paySubTitle && (
                <Tooltip
                  content={
                    rewardBands ? (
                      <RewardBandsTooltip rewardBands={rewardBands} />
                    ) : undefined
                  }
                  position={
                    rewardBands && isArnoldBrosRewardBands(rewardBands)
                      ? 'left'
                      : undefined
                  }
                >
                  <Subtitle
                    style={{
                      color:
                        isPassed && isEligible
                          ? undefined
                          : 'var(--color-text-muted)',
                      cursor: rewardBands ? 'help' : 'unset',
                    }}
                  >
                    {paySubTitle}
                  </Subtitle>
                </Tooltip>
              )}
        </Row>
      )}
    </>
  );
};

const RewardHeading = ({
  isEligible,
  label,
  currentPay,
  paySubTitle,
  isPassed,
  rewardBands,
  isLoadingPreview,
  rewardType,
  subtitleNode,
  isOverridden,
  payInfoTooltip,
  payInfoTooltipTestId,
  rewardBandsTooltipTestId,
}: {
  isEligible: boolean;
  label: string;
  currentPay: string;
  paySubTitle?: string;
  isPassed: boolean;
  rewardBands?:
    | BonusTypes.Rewards.RewardBands
    | BonusTypes.Rewards.ArnoldBrosRewardBands;
  isLoadingPreview: boolean;
  rewardType: 'none' | 'points' | 'monetary';
  // When provided, replaces the static rate subtitle under the pay figure (e.g. the
  // override-mode reward-payout dropdown).
  subtitleNode?: ReactNode;
  // A reward-level payout override is active on this line (staged or persisted).
  isOverridden?: boolean;
  // Info affordance next to the pay figure (e.g. an aggregated reward's component breakdown).
  payInfoTooltip?: ReactNode;
  payInfoTooltipTestId?: string;
  rewardBandsTooltipTestId?: string;
}) => {
  return (
    <Row spaceBetween centerAlign>
      <Row centerAlign style={{ gap: 8 }}>
        <Icon
          icon={isPassed ? 'checkmark' : 'cross'}
          color={
            isPassed
              ? 'var(--color-status-success)'
              : 'var(--color-status-danger)'
          }
          height={20}
          width={20}
        />
        <Typography.Header type={'H3'}>{label}</Typography.Header>
      </Row>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-end',
          height: 48,
        }}
      >
        {isLoadingPreview && <Loading isSmall />}
        {!isLoadingPreview && (
          <RewardPay
            isEligible={isEligible}
            isPassed={isPassed}
            currentPay={currentPay}
            paySubTitle={paySubTitle}
            subtitleNode={subtitleNode}
            rewardBands={rewardBands}
            rewardType={rewardType}
            isOverridden={isOverridden}
            payInfoTooltip={payInfoTooltip}
            payInfoTooltipTestId={payInfoTooltipTestId}
            rewardBandsTooltipTestId={rewardBandsTooltipTestId}
          />
        )}
      </div>
    </Row>
  );
};

export default RewardHeading;
